import { useRef, useState } from "react";
import { CircleNotch, FilmSlate, LinkSimple, UploadSimple, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { MediaLibraryDialog } from "@/components/ui/media-picker";
import { parseVideoEmbed } from "@/lib/video-embed";
import { toast } from "@/lib/toast";
import type { VideoSourceType } from "@/types";

const MAX_UPLOAD = 500 * 1024 * 1024;

interface VideoSourceFieldProps {
  sourceType: VideoSourceType;
  videoUrl: string;
  onChange: (next: { sourceType: VideoSourceType; videoUrl: string }) => void;
}

/** XHR so a multi-hundred-megabyte upload shows real progress. */
function uploadWithProgress(file: File, onProgress: (pct: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", file);
    form.append("folder", "videos");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(body.fileUrl as string);
        else reject(new Error(body.error || "Upload nije uspio"));
      } catch {
        reject(new Error("Upload nije uspio"));
      }
    };
    xhr.onerror = () => reject(new Error("Upload nije uspio"));
    xhr.open("POST", "/api/upload?folder=videos");
    xhr.send(form);
  });
}

export function VideoSourceField({ sourceType, videoUrl, onChange }: VideoSourceFieldProps) {
  const [progress, setProgress] = useState<number | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const embed = sourceType === "embed" && videoUrl ? parseVideoEmbed(videoUrl) : null;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD) {
      toast.error("Video je veći od 500 MB.");
      return;
    }
    setProgress(0);
    try {
      const url = await uploadWithProgress(file, setProgress);
      onChange({ sourceType: "upload", videoUrl: url });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload nije uspio");
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const tab = (type: VideoSourceType, label: string, Icon: typeof UploadSimple) => (
    <button
      type="button"
      onClick={() => sourceType !== type && onChange({ sourceType: type, videoUrl: "" })}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
        sourceType === type ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex rounded-xl bg-muted p-1">
        {tab("upload", "Upload fajla", UploadSimple)}
        {tab("embed", "YouTube / Vimeo", LinkSimple)}
      </div>

      {sourceType === "upload" ? (
        videoUrl ? (
          <div className="group relative overflow-hidden rounded-xl border bg-black">
            <video src={videoUrl} controls preload="metadata" className="aspect-video w-full" />
            <button
              type="button"
              onClick={() => onChange({ sourceType: "upload", videoUrl: "" })}
              className="absolute right-2 top-2 rounded-lg bg-white/90 p-1.5 text-rose-600 shadow-sm"
              aria-label="Ukloni video"
            >
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border/60 py-10">
            <input
              ref={inputRef}
              type="file"
              accept="video/mp4,video/webm"
              className="hidden"
              id="video-source-file"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {progress !== null ? (
              <div className="w-full max-w-xs space-y-2 px-4 text-center">
                <CircleNotch className="mx-auto size-7 animate-spin text-primary/50" />
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs tabular-nums text-muted-foreground">{progress}%</p>
              </div>
            ) : (
              <>
                <FilmSlate className="size-8 text-muted-foreground/40" />
                <div className="flex gap-2">
                  <label htmlFor="video-source-file" className="cursor-pointer rounded-lg border bg-background px-3 py-1.5 text-xs font-medium shadow-sm hover:bg-muted">
                    <UploadSimple className="mr-1 inline size-3.5" />
                    Odaberi fajl
                  </label>
                  <button type="button" onClick={() => setLibraryOpen(true)} className="rounded-lg border bg-background px-3 py-1.5 text-xs font-medium shadow-sm hover:bg-muted">
                    Biblioteka
                  </button>
                </div>
                <p className="text-xs text-muted-foreground/60">MP4 ili WebM · maks. 500 MB</p>
              </>
            )}
          </div>
        )
      ) : (
        <div className="space-y-2">
          <Input
            value={videoUrl}
            onChange={(e) => onChange({ sourceType: "embed", videoUrl: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=…"
            inputMode="url"
          />
          {videoUrl && !embed && <p className="text-xs text-rose-600">Link nije prepoznat. Podržani su YouTube i Vimeo.</p>}
          {embed && (
            <div className="overflow-hidden rounded-xl border bg-black">
              <iframe src={embed.embedUrl} title="Pregled" className="aspect-video w-full" allowFullScreen loading="lazy" />
            </div>
          )}
        </div>
      )}

      {libraryOpen && (
        <MediaLibraryDialog
          folder="videos"
          onSelect={(url) => {
            onChange({ sourceType: "upload", videoUrl: url });
            setLibraryOpen(false);
          }}
          onClose={() => setLibraryOpen(false)}
        />
      )}
    </div>
  );
}
